import React, { useState } from 'react';

/**
 * Shared helpers used by both card variants.
 */

/** Render star icons based on numeric rating. */
export const StarRating = ({ rating }) => {
  const full = Math.floor(rating || 0);
  const half = (rating || 0) - full >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;

  return (
    <span className="tp-stars" aria-label={`${rating} stars`}>
      {'★'.repeat(full)}
      {half ? '½' : ''}
      {'☆'.repeat(empty)}
    </span>
  );
};

/** Return initials from a full name string. */
export const getInitials = (name = '') =>
  name
    .trim()
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');

/** Convert country code to flag emoji */
export const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return null;
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/** Status badge – ONLINE or AWAY. */
export const StatusBadge = ({ status }) => {
  if (!status || status === 'offline') return null;
  const isAway = status === 'away';
  return (
    <span className={`tp-status-badge ${isAway ? 'tp-status-badge--away' : ''}`}>
      {isAway ? 'Away' : 'Online'}
    </span>
  );
};

/** Verified check icon. */
export const VerifiedIcon = () => (
  <i className="bi bi-patch-check-fill tp-verified" title="Verified Teacher" />
);

/** Language tag pill. */
export const Tag = ({ label }) => (
  <span className="tp-tag">{label}</span>
);

/** Wishlist heart button. */
export const WishlistButton = ({ saved, onToggle }) => {
  return (
    <button
      className={`tp-wishlist-btn ${saved ? 'tp-wishlist-btn--saved' : ''}`}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      title={saved ? 'Remove from wishlist' : 'Save teacher'}
      aria-label={saved ? 'Remove from wishlist' : 'Save teacher'}
    >
      <i className={`bi ${saved ? 'bi-heart-fill' : 'bi-heart'}`} />
    </button>
  );
};

/**
 * TeacherCardFeatured
 * Narrow card used in the horizontal featured carousel.
 *
 * Props:
 *   teacher    – teacher data object from API
 *   onView     – () => void – navigate to teacher profile
 *   onMessage  – () => void
 *   onBook     – () => void
 */
export const TeacherCardFeatured = ({ teacher, onView, onMessage, onBook }) => {
  const [saved, setSaved] = useState(false);

  const name = teacher?.full_name || teacher?.name || 'Teacher';
  const avatar = teacher?.profile_picture || teacher?.avatar || null;
  const rating = parseFloat(teacher?.rating || teacher?.avg_rating || 4.5).toFixed(1);
  const reviews = teacher?.review_count || teacher?.reviews || 0;
  const students = teacher?.follower_count || teacher?.students || 0;
  const price = teacher?.lesson_price || teacher?.price || 0;
  const languages = teacher?.languages || [];
  const tags = teacher?.skills || teacher?.tags || [];
  const isOnline = teacher?.is_online;
  const isAway = teacher?.is_away;
  const isVerified = teacher?.is_blue_verified || teacher?.is_verified || false;
  const flag = teacher?.country_flag || '🌍';
  const countryLang = languages.slice(0, 2).join(' · ') || 'English';

  const status = isOnline ? 'online' : isAway ? 'away' : null;

  return (
    <div className="tp-card-featured" onClick={onView} role="button" tabIndex={0}>

      {/* Avatar */}
      <div className="tp-card-featured__avatar-wrap">
        <StatusBadge status={status} />
        <WishlistButton saved={saved} onToggle={() => setSaved((s) => !s)} />

        {avatar ? (
          <img src={avatar} alt={name} className="tp-card-featured__avatar" />
        ) : (
          <div className="tp-card-featured__avatar-placeholder">
            <span>{getInitials(name)}</span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="tp-card-featured__body">

        <div className="tp-card-featured__name-row">
          <span className="tp-card-featured__name" title={name}>{name}</span>
          {isVerified && <VerifiedIcon />}
        </div>

        <div className="tp-card-featured__flag-lang">
          <span className="flag">{flag}</span>
          <span>{countryLang}</span>
        </div>

        {tags.length > 0 && (
          <div className="tp-card-featured__tags">
            {tags.slice(0, 3).map((t) => <Tag key={t} label={t} />)}
          </div>
        )}

        <div className="tp-card-featured__rating-row">
          <StarRating rating={rating} />
          <span className="tp-rating-num">{rating}</span>
          <span className="tp-review-count">({reviews})</span>
        </div>

        <div className="tp-card-featured__students">
          {students.toLocaleString()} Students
        </div>

        <div className="tp-card-featured__price-row">
          <div className="tp-card-featured__price">
            ${price}
            <span className="tp-card-featured__per-lesson"> / lesson</span>
          </div>
        </div>

        <div className="tp-card-featured__actions">
          <button className="tp-btn-message" onClick={(e) => { e.stopPropagation(); onMessage?.(); }}>
            Message
          </button>
          <button className="tp-btn-book" onClick={(e) => { e.stopPropagation(); onBook?.(); }}>
            Book Lesson
          </button>
        </div>

      </div>
    </div>
  );
};

export const TeacherCardGrid = ({ teacher, onView }) => {
  const name = teacher?.full_name || teacher?.name || 'Teacher';
  const avatar = teacher?.profile_picture || teacher?.avatar || null;
  const rating = parseFloat(teacher?.rating || teacher?.avg_rating || 5.0).toFixed(1);
  const reviews = teacher?.review_count || teacher?.reviews || 0;
  const languages = teacher?.languages || [];
  const tags = teacher?.skills || teacher?.tags || [];
  const isOnline = teacher?.is_online;
  const isAway = teacher?.is_away;
  const flag = teacher?.country_flag || getFlagEmoji(teacher?.country_code) || getFlagEmoji(teacher?.country?.code) || '🌍';
  const countryName = typeof teacher.country === 'object'
    ? teacher.country?.name
    : (teacher.country || teacher.country_name || '');

  const firstLang = languages[0] || 'English';
  const countryLang = languages.length > 1 ? `${firstLang} + ${languages.length - 1}` : firstLang;
  const status = isOnline ? 'online' : isAway ? 'away' : null;
  const isVerified = teacher?.is_blue_verified || teacher?.is_verified || false;

  return (
    <div className="tp-card-grid-new" onClick={onView} role="button" tabIndex={0}>
      {/* Left Column */}
      <div className="tp-card-grid-new__left">
        <div className="tp-card-grid-new__avatar-container">
          {avatar ? (
            <img src={avatar} alt={name} className="tp-card-grid-new__avatar" />
          ) : (
            <div className="tp-card-grid-new__avatar-placeholder">
              <span>{getInitials(name)}</span>
            </div>
          )}
        </div>

        {/* Skills under Avatar */}
        <div className="tp-card-grid-new__tags">
          {tags.slice(0, 2).map((t) => (
            <span key={t} className="tp-card-grid-new__tag">{t}</span>
          ))}
        </div>

        {/* View Profile Link */}
        <button
          className="tp-card-grid-new__view-profile"
          onClick={(e) => { e.stopPropagation(); onView?.(); }}
        >
          <i className="bi bi-person-fill" style={{ marginRight: '4px' }} /> View Profile
        </button>
      </div>

      {/* Right Column */}
      <div className="tp-card-grid-new__right">
        {/* Online/Away Status Indicator */}
        <div className="tp-card-grid-new__status-indicator">
          {status === 'online' && (
            <span className="status-online"><span className="status-dot"></span>Online</span>
          )}
          {status === 'away' && (
            <span className="status-away"><span className="status-dot"></span>Away</span>
          )}
          {!status && (
            <span className="status-offline"><span className="status-dot"></span>Offline</span>
          )}
        </div>

        {/* Name */}
        <div className="tp-card-grid-new__name-row">
          <h3 className="tp-card-grid-new__name" title={name}>{name}</h3>
          {isVerified && <VerifiedIcon />}
        </div>

        {/* Flag + Country name */}
        <div className="tp-card-grid-new__country-row">
          <span className="flag-icon">{flag}</span>
          <span className="country-name">{countryName}</span>
        </div>

        {/* Language */}
        <div className="tp-card-grid-new__language-row">
          {countryLang}
        </div>

        {/* Rating Stars + Count */}
        <div className="tp-card-grid-new__rating-row">
          <StarRating rating={rating} />
          <span className="rating-count">({reviews})</span>
        </div>
      </div>
    </div>
  );
};
